import {
  adaptiveCenterModalOptions,
  ModalWrapper,
  ModalWrapperProps,
} from '@psl/appComponents/ModalWrapper';
import { PageMeta } from '@psl/appComponents/seo/PageMeta';
import { useIsMobileLayout } from '@psl/components/styles/responsive';
import { useRoute } from '@react-navigation/core';
import { useNavigation } from '@react-navigation/native';
import {
  StackNavigationOptions,
  StackNavigationProp,
} from '@react-navigation/stack';
import {
  ComponentProps,
  FC,
  memo,
  ReactNode,
  Suspense,
  useEffect,
  useMemo,
} from 'react';
import { useWindowDimensions } from 'react-native';
import { PageLoading } from '../../../appComponents/PageLoading';
import { RxAutoHangUpProviderForPage } from '../reactNavigationHelpers';

export type WrappedNavigatorScreen<P extends object> = FC<void> & {
  component: FC<P>;
  options: StackNavigationOptions;
  extraProps?: any;
};

export interface WrapNavigatorScreenOptions extends StackNavigationOptions {
  suspenseFallback?: ReactNode;
}

export type NavigatorScreenExtraProps = {
  title?: string;
  description?: string;
  modal?:
    | Omit<ModalWrapperProps, 'children'>
    | boolean
    | ((props: {
        vw: number;
        vh: number;
      }) => Omit<ModalWrapperProps, 'children'>);
} & Record<string, any>;

export function wrapNavigatorScreen<P extends object>(
  Component: FC<P>,
  options?:
    | WrapNavigatorScreenOptions
    | ((props: {
        route: { params: P };
        navigation: StackNavigationProp<any>;
      }) => WrapNavigatorScreenOptions),
  extraProps?: NavigatorScreenExtraProps,
): WrappedNavigatorScreen<P> {
  const { title, description, modal, ...extraScreenProps } = extraProps ?? {};

  const NavigatorScreenWrapper = memo(function () {
    const { width: vw, height: vh } = useWindowDimensions();
    const isMobile = useIsMobileLayout();
    const route = useRoute() as unknown as { params: P };
    const navigation = useNavigation<StackNavigationProp<any>>();
    const navOptions = useMemo(
      () =>
        typeof options === 'function'
          ? options({ route, navigation })
          : options,
      [navigation, route],
    );

    const preferModal = modal != null;

    useEffect(() => {
      if (!isMobile && preferModal) {
        navigation.setOptions({
          headerShown: false,
          presentation: 'transparentModal',
          cardStyle: {
            backgroundColor: 'transparent',
          },
        });
      }
    }, [isMobile, navigation, preferModal]);

    const renderPage = () => {
      return (
        <RxAutoHangUpProviderForPage>
          {title || description ? (
            <PageMeta title={title} description={description} />
          ) : null}
          <Suspense fallback={navOptions?.suspenseFallback ?? null}>
            <Component {...route.params} />
          </Suspense>
        </RxAutoHangUpProviderForPage>
      );
    };

    if (!modal || isMobile) {
      return renderPage();
    }

    const modalProps =
      typeof modal === 'function'
        ? modal({ vw, vh })
        : typeof modal === 'boolean'
          ? {}
          : modal;

    return (
      <ModalWrapper dismissible={true} {...modalProps}>
        {renderPage()}
      </ModalWrapper>
    );
  }) as any;

  NavigatorScreenWrapper.displayName = Component.displayName ?? Component.name;
  NavigatorScreenWrapper.component = Component;
  NavigatorScreenWrapper.options = options as StackNavigationOptions;
  NavigatorScreenWrapper.extraProps = extraScreenProps;

  if (modal) {
    NavigatorScreenWrapper.options = adaptiveCenterModalOptions(
      NavigatorScreenWrapper.options,
    );
  }

  return NavigatorScreenWrapper;
}

export function wrapModalNavigatorScreen<P extends object>(
  Component: FC<P>,
  options?:
    | WrapNavigatorScreenOptions
    | ((props: {
        route: { params: P };
        navigation: StackNavigationProp<any>;
      }) => WrapNavigatorScreenOptions),
  extraProps?: NavigatorScreenExtraProps,
): WrappedNavigatorScreen<P> {
  return wrapNavigatorScreen(Component, options, {
    ...extraProps,
    modal: !extraProps?.modal ? true : extraProps?.modal,
  });
}

export type ScreenToParams<
  Screens extends { [k: string]: WrappedNavigatorScreen<any> },
> = {
  [P in keyof Screens]: ComponentProps<Screens[P]['component']>;
};

export function spreadScreens(
  StackScreen: any,
  screens: {
    [k: string]: WrappedNavigatorScreen<any>;
  },
  initialScreen?: string,
  initialParams?: any,
) {
  return Object.entries(screens).map(([name, Component]) => (
    <StackScreen
      key={name}
      name={name as any}
      component={Component}
      options={Component.options}
      initialParams={name === initialScreen ? initialParams : undefined}
      {...Component.extraProps}
    />
  ));
}

export function wrapLazyNavigatorScreen<P extends object>(
  Component: FC<P>,
  options?:
    | WrapNavigatorScreenOptions
    | ((props: {
        route: { params: P };
        navigation: StackNavigationProp<any>;
      }) => WrapNavigatorScreenOptions),
  extraProps?: NavigatorScreenExtraProps,
): WrappedNavigatorScreen<P> {
  const wrappedOptions =
    typeof options === 'function'
      ? (props: {
          route: { params: P };
          navigation: StackNavigationProp<any>;
        }) => {
          const ret = options(props);
          return {
            suspenseFallback: <PageLoading />,
            ...ret,
          };
        }
      : {
          suspenseFallback: <PageLoading />,
          ...options,
        };
  return wrapNavigatorScreen(Component, wrappedOptions, extraProps);
}

export function wrapLazyModalNavigatorScreen<P extends object>(
  Component: FC<P>,
  options?:
    | WrapNavigatorScreenOptions
    | ((props: {
        route: { params: P };
        navigation: StackNavigationProp<any>;
      }) => WrapNavigatorScreenOptions),
  extraProps?: NavigatorScreenExtraProps,
): WrappedNavigatorScreen<P> {
  return wrapLazyNavigatorScreen(Component, options, {
    ...extraProps,
    modal: !extraProps?.modal ? true : extraProps?.modal,
  });
}
